import React, { useState } from 'react';
import { DeleteOutlined } from '@ant-design/icons';
import {
	useMutation,
	useQueryClient,
} from 'react-query';
import { Moment } from 'moment';
import { formatNumber } from '@shared/utils/formatNumber';
import { Currencies } from '@shared/utils/constants';
import { Values } from '@shared/utils/objectEnums';
import type { GetEuasResponse } from '@api/features/voyages/euas/getEuas';
import { TcTermsWithIncludes } from '@api/utils/getTcTerms';
import Card from '@client/components/Card/Card';
import Table from '@client/components/Table/Table';
import Button from '@client/components/Button';
import AddButton from '@client/components/AddButton';
import { deleteHireInvoice } from '@client/lib/untyped-api';
import { getInvoiceIdentifierColumn } from '@client/screens/fleet/VoyageDetailsScreen/helpers/tableColumns';
import showErrorNotification from '@client/utils/showErrorNotification';
import EuaInvoiceForm from './EuaInvoiceForm';

// Minimal types to avoid expensive API type inference
type EuaInvoiceRow = {
	id: number;
	invoiceDate: Moment;
	amount: number;
	paid?: number;
	outstanding: number;
	HireInvoiceItems: Array<{
		HIIEua: {
			euaItemId: number;
		}
	}>
};

type BankAccountSlice = {
	id: number;
	currency: Values<typeof Currencies>;
	name: string | null;
	vesselId: number | null;
	businessUnit: string | null;
};

export type VoyageDetailsFields = {
	id: number;
	tcTerms: TcTermsWithIncludes;
	euaInvoices: EuaInvoiceRow[];
	bankAccount: BankAccountSlice;
	allBankAccounts: BankAccountSlice[];
};

const getColumns = ({
	voyageDetails,
	onDelete,
}: {
	voyageDetails: VoyageDetailsFields;
	onDelete: (id: number) => void;
}) => {
	return [
		getInvoiceIdentifierColumn(voyageDetails),
		{
			title: 'Invoice Date',
			dataIndex: 'invoiceDate',
		},
		{
			title: 'EUAs',
			dataIndex: 'amount',
			render: (amount: number) => formatNumber(amount, { separateThousands: true }),
		},
		{
			title: 'Total payments',
			dataIndex: 'paid',
			render: (paid: number) => formatNumber(paid, { separateThousands: true }),
		},
		{
			title: 'Balance',
			dataIndex: 'outstanding',
			render: (outstanding: number) => formatNumber(outstanding, { separateThousands: true }),
		},
		{
			dataIndex: '',
			title: '',
			align: 'center' as const,
			render: (_v: any, row: EuaInvoiceRow) => {
				return (
					<div>
						<Button
							confirmTitle="Are you sure you want to delete this invoice?"
							onClick={() => onDelete(row.id)}
							type="link"
							icon={(<DeleteOutlined />)}
							danger
						/>
					</div>
				);
			},
		},
	];
};

type Props = {
	voyageDetails: VoyageDetailsFields;
	refresh: () => void;
	euas: GetEuasResponse | undefined;
};

const EuaInvoiceTable = ({
	voyageDetails,
	refresh,
	euas,
}: Props) => {
	const [euaInvoiceFormOpen, setEuaInvoiceFormOpen] = useState(false);
	const client = useQueryClient();
	const invalidate = () => client.invalidateQueries('euas');

	const refreshEverything = () => {
		refresh();
		invalidate();
	};

	const deleteHireInvoiceMutation = useMutation(
		deleteHireInvoice,
		{
			onSuccess: refreshEverything,
			onError: (error: Error) => showErrorNotification(error.toString()),
		},
	);

	const deleteEuaInvoice = (id: number) => deleteHireInvoiceMutation.mutate(id);

	return (
		<>
			<EuaInvoiceForm
				voyageDetails={voyageDetails}
				open={euaInvoiceFormOpen}
				onClose={() => setEuaInvoiceFormOpen(false)}
				onInvoiceCreated={refreshEverything}
				euas={euas ?? []}
			/>
			<Card
				slim
				title="EUA Invoices"
				extra={(
					<AddButton
						onClick={() => setEuaInvoiceFormOpen(true)}
					>
						New Invoice
					</AddButton>
				)}
			>
				<Table<EuaInvoiceRow>
					dataSource={voyageDetails.euaInvoices}
					columns={getColumns({
						onDelete: deleteEuaInvoice,
						voyageDetails,
					})}
					pagination={false}
				/>
			</Card>
		</>

	);
};

export default EuaInvoiceTable;
